import { useState, useEffect } from 'react';
import Input from './Input';
import DeleteForever from '../../assets/screening/DeleteForever';
import { validateEmail, validateName } from '../../validations/screeningValidation';
import './FormRow.scss';
import { t } from '../../i18n/i18n';
import { DatePicker } from 'antd';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { format } from 'date-fns';

const FormRow = ({
  setError,
  allErrors,
  rowIndex,
  handleValidation,
  remove,
  duplicatedEmails = [],
  data = {},
  emailError,
  firstNameError,
  lastNameError,
  setData,
  duplicatedInvitations,
  setDuplicatedInvitations,
}) => {
  const [errors, setErrors] = useState({ firstName: false, lastName: false, email: false });
  const [validate, setValidate] = useState(0);
  const [duplicatedEmail, setDuplicatedEmail] = useState(false);
  const { selectedSearch } = useSelector((state) => state.screenings);

  useEffect(() => {
    if (handleValidation) setValidate((value) => value + 1);
  }, [handleValidation]);

  useEffect(() => {
    setDuplicatedEmail(duplicatedEmails.includes(data.email?.toLowerCase()));
  }, [duplicatedEmails]);

  useEffect(() => {
    if (validate && Object.keys(data).length) {
      setErrors({
        firstName: !validateName(data.firstName),
        lastName: !validateName(data.lastName),
        email: !validateEmail(data.email),
      });
    }
  }, [validate]);

  const baseHandler = (value, key, validationFn) => {
    if (Object.keys(allErrors).includes(String(rowIndex))) {
      setErrors((obj) => ({ ...obj, [key]: !validationFn(value) }));
    }
    setData((obj) => ({ ...obj, [rowIndex]: { ...data, [key]: value } }));
  };

  const handleFirstName = (value) => {
    baseHandler(value, 'firstName', validateName);
  };

  const handleLastName = (value) => {
    baseHandler(value, 'lastName', validateName);
  };

  const handleEmail = (value) => {
    baseHandler(value, 'email', validateEmail);
    setDuplicatedInvitations(false);
  };

  const handleInvitationLimitDate = (value) => {
    if (!value) {
      baseHandler(null, 'limitDate', () => true);
      return;
    }

    // Convert moment to ISO string, preserving the selected time
    const isoString = moment(value).toISOString();
    baseHandler(isoString, 'limitDate', () => true);
  };

  useEffect(() => {
    if (validate) {
      const hasErrors = Object.values(errors).some((value) => value);
      setError((value) => ({ ...value, [rowIndex]: hasErrors }));
    }
  }, [errors]);

  const hasFirstNameErrors =
    errors.firstName || (!validateName(data.firstName) && firstNameError)
      ? t('SCREENING_NAME_ERROR')
      : null;

  const hasLastNameErrors =
    errors.lastName || (!validateName(data.lastName) && lastNameError)
      ? t('SCREENING_NAME_ERROR')
      : null;

  const hasEmailErrors =
    errors.email || (!validateEmail(data.email) && emailError)
      ? t('SCREENING_EMAIL_ERROR')
      : duplicatedEmail
      ? t(`${duplicatedInvitations ? 'SCREENING_INVITATION_REPEATED' : 'SCREENING_EMAIL_REPEATED'}`)
      : null;

  // Get timezone string using date-fns
  const now = new Date();
  let timezoneOffset = '-03:00';
  let timezoneDisplay = 'UTC-03:00';

  try {
    timezoneOffset = format(now, 'xxx'); // Returns offset like "-03:00"
    timezoneDisplay = `UTC${timezoneOffset}`; // "UTC-03:00"
  } catch (error) {
    // Fallback to default timezone if format fails
  }

  // Format with timezone appended - using square brackets to display as literal
  const formatWithTimezone = `DD-MM-YYYY HH:mm [(${timezoneDisplay})]`;

  return (
    <div data-testid="form-row-send-emails" className="d-flex my-3 flex-wrap">
      <Input
        placeholder={t('SCREENING_FIRSTNAME_PLACEHOLDER')}
        className="pr-2 pr-md-3 flex-grow-1 w-25"
        name="firstName"
        value={data.firstName}
        onChange={handleFirstName}
        error={hasFirstNameErrors}
      />
      <Input
        placeholder={t('SCREENING_LASTNAME_PLACEHOLDER')}
        className="pr-sm-2 pr-md-3 flex-grow-1 w-25"
        name="lastName"
        value={data.lastName}
        onChange={handleLastName}
        error={hasLastNameErrors}
      />
      <Input
        placeholder={t('SCREENING_EMAIL_PLACEHOLDER')}
        className="flex-grow-3 mt-2 mt-sm-0 w-75 w-sm-25"
        name="email"
        value={data.email}
        onChange={handleEmail}
        error={hasEmailErrors}
      />
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{t('DASHBOARD_SEARCH_HEADERS_LIMIT_DATE')}</Tooltip>}
      >
        <div className="px-2">
          <DatePicker
            showTime
            placeholder={`${t('DASHBOARD_SEARCH_HEADERS_LIMIT_DATE')} (${timezoneDisplay})`}
            onOk={handleInvitationLimitDate}
            onChange={handleInvitationLimitDate}
            format={formatWithTimezone}
            showSecond={false}
            value={data.limitDate ? moment(data.limitDate) : null}
            disabledDate={(current) => {
              return (
                current < moment().add(2, 'hours') ||
                current >= moment(selectedSearch.stages ? selectedSearch.stages[0]?.endDate : null)
              );
            }}
            className="rounded calendar-invitations h-100"
          />
        </div>
      </OverlayTrigger>
      <button
        type="button"
        className="screening-input-delete-icon px-2"
        tabIndex={-1}
        onClick={() => remove(rowIndex)}
        data-testid={`button-delete-row-form-${rowIndex}`}
      >
        <DeleteForever />
      </button>
    </div>
  );
};

export default FormRow;
